import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import { formatImageUrl } from "@/lib/general";

interface ImageGalleryModalProps {
  images: string[];
  isOpen: boolean;
  onClose: () => void;
  alt: string;
  hotelName: string;
}

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
  images,
  isOpen,
  onClose,
  alt,
  hotelName,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 bg-opacity-50 flex justify-center items-center"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={backdropVariants}
        >
          <motion.div
            className="relative w-full h-full bg-white flex flex-col pb-4 px-5 overflow-hidden rounded-md"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ type: "spring", stiffness: 200, damping: 25 }}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-700"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {/* Optional Header */}
            <div className="flex flex-row items-center justify-start gap-4 py-4">
              <h2 className="text-lg font-medium text-neutral-800 max-w-[200px] sm:max-w-[300px] md:max-w-[400px] truncate">
                {hotelName}
              </h2>
              <div className="w-1 h-1 bg-neutral-500 rounded-full"></div>
              <p className="text-sm text-neutral-500 font-light">
                {images.length} images
              </p>
            </div>

            {/* Image Grid - Scrollable Area */}
            <div className="flex-1 overflow-auto no-scrollbar rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                {images.map((src, index) => (
                  <div key={index} className="w-full h-auto">
                    <LazyLoad
                      src={formatImageUrl(src, "medium")}
                      lowResSrc={formatImageUrl(src, "low")}
                      alt={alt}
                      className="object-cover w-full h-auto rounded-md"
                    />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageGalleryModal;
